<template>
  <section class="integration-stack">
    <DetailCard title="Attempts">
      <p class="integration-muted">
        This is the operation history. Open the linked integration log to inspect the saved provider request and response.
      </p>
      <div v-if="loading" class="integration-skeleton" role="status" aria-label="Loading attempts">
        <span v-for="index in 3" :key="index" />
      </div>
      <div v-else-if="error" class="integration-error" role="alert">
        {{ error }}
        <AppButton design="new" theme="dark" size="small" @click.native="loadList">Retry attempts</AppButton>
      </div>
      <template v-else-if="list">
        <p v-if="!list.results.length">No provider calls recorded.</p>
        <div v-else class="integration-attempt-list">
          <article v-for="attempt in list.results" :key="attempt.id" class="integration-attempt">
            <div class="integration-attempt__heading">
              <strong>#{{ attempt.sequence }}</strong>
              <AppStatusBadge design="new" compact :tone="color(attempt.status)">{{ humanize(attempt.status) }}</AppStatusBadge>
              <span class="integration-muted">{{ date(attempt.started_at, true) }}</span>
            </div>
            <Metadata
              :fields="{
                Initiator: attempt.initiator && attempt.initiator.label,
                Finished: date(attempt.finished_at, true),
                'Duration (ms)': attempt.duration_ms,
                Result: attempt.result && attempt.result.message,
                'Result code': attempt.result && attempt.result.code,
                'HTTP status': attempt.result && attempt.result.http_status,
                'Provider message ID': attempt.provider_message_id,
              }"
            />
            <router-link v-if="attempt.log && canOpenLogs" class="integration-log-link" :to="logRoute(attempt.log)">
              <img src="@/assets/img/snacks-link.svg" alt="" aria-hidden="true">
              <span>View integration log</span>
            </router-link>
            <span v-else-if="!attempt.log" class="integration-muted">No native integration log was linked.</span>
          </article>
        </div>
        <AppPagination
          design="new"
          placement="inline"
          :loading="loading"
          :page="page"
          :pages="list.pages"
          :count="list.count"
          :visible-count="list.results.length"
          :page-size="40"
          label="Attempt pages"
          @change="changePage"
        />
      </template>
    </DetailCard>
  </section>
</template>

<script>
import { getMessageOperationAttempts } from '@/http'
import Metadata from './Metadata'
import DetailCard from './DetailCard'
import { techSupportKey } from '@/utils/techSupportAccess'
import { dateLabel, humanize, tone, errorMessage, normalizePage } from './model'

export default {
  components: { Metadata, DetailCard },
  props: { detail: Object, refreshKey: Number },
  data: () => ({ list: null, page: 1, loading: false, error: '', generation: 0 }),
  computed: {
    canOpenLogs() { return this.$store.getters['permissions/can'](techSupportKey('integration_logs')) }
  },
  watch: { refreshKey() { this.loadList() } },
  created() { this.loadList() },
  beforeDestroy() { this.generation++ },
  methods: {
    date: dateLabel,
    humanize,
    color: tone,
    logRoute(log) {
      return { name: `tech-support-integration-logs-${log.integration}`, query: { log: log.id } }
    },
    async loadList() {
      const generation = ++this.generation
      this.loading = true
      this.error = ''
      try {
        const response = await getMessageOperationAttempts(this.detail.id, { page: this.page, page_size: 40 })
        if (generation === this.generation) this.list = normalizePage(response.data)
      } catch (error) {
        if (generation !== this.generation) return
        if (error?.response?.status === 403) this.$emit('denied')
        if (error?.response?.status === 404 && this.page > 1) {
          try {
            const first = await getMessageOperationAttempts(this.detail.id, { page: 1, page_size: 40 })
            if (generation !== this.generation) return
            const { pages } = normalizePage(first.data)
            if (pages < this.page) { this.page = pages; this.loadList(); return }
          } catch (pageError) {
            if (generation === this.generation) this.error = errorMessage(pageError, 'Attempts')
          }
        }
        if (generation === this.generation && !this.error) this.error = errorMessage(error, 'Attempts')
      } finally {
        if (generation === this.generation) this.loading = false
      }
    },
    changePage(page) { this.page = page; this.loadList() }
  }
}
</script>
